import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { errMsg } from "./mcfUtils";

// Admin-only controlled stock adjustment (signed qty + mandatory reason). Never edits source records.
export function McfAdjustDialog({ warehouse, target, products, onClose, onSaved }) {
  const [sku, setSku] = useState("");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSku(target?.sku_id || "");
    setQty("");
    setReason("");
  }, [target]);

  if (!target) return null;
  const options = products.map((p) => ({ value: p.sku_id, label: `${p.product_name} (${p.sku_id})`, searchTerms: [p.item_code] }));

  const save = async () => {
    const n = Number(qty);
    if (!sku) return toast.error("Select a SKU");
    if (!n) return toast.error("Enter a non-zero quantity (use − to reduce stock)");
    if (reason.trim().length < 3) return toast.error("A reason is required");
    setSaving(true);
    try {
      await axios.post(`${API}/mcf-inventory/adjustments`, { warehouse_id: warehouse.id, sku_id: sku, qty: n, reason: reason.trim() });
      toast.success("Adjustment recorded");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, "Could not save adjustment"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-md" data-testid="mcf-adjust-dialog">
        <DialogHeader>
          <DialogTitle>Stock Adjustment — {warehouse.warehouse_name}</DialogTitle>
          <DialogDescription>Adds a signed correction to Delivered Qty. Source transfers and invoices are not changed. Every adjustment is audited.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>SKU *</Label>
            {target.sku_id
              ? <p className="text-sm font-medium" data-testid="mcf-adjust-sku-fixed">{target.sku_name} <span className="font-mono text-xs text-muted-foreground">({target.sku_id})</span></p>
              : <SearchableSelect options={options} value={sku} onValueChange={setSku} placeholder="Select SKU" data-testid="mcf-adjust-sku-select" />}
          </div>
          <div className="space-y-1">
            <Label>Quantity * <span className="text-xs text-muted-foreground font-normal">(+ adds stock, − removes)</span></Label>
            <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="e.g. 10 or -5" data-testid="mcf-adjust-qty" />
          </div>
          <div className="space-y-1">
            <Label>Reason *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Physical count correction on 18 Oct" data-testid="mcf-adjust-reason" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1F8F88] hover:bg-[#197a74] text-white" data-testid="mcf-adjust-save">Save Adjustment</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
